import Vue from 'vue'

var MANUAL_ALLOCATIONS_QUERY_STORAGE_KEY = 'manualAllocations.queryByCustomer'
var DEFAULT_STATUS_FILTER_VALUES = ['Unallocated', 'PartiallyAllocated']

function getDefaultQuery () {
  return {
    page: 1,
    pageSize: 10,
    sortBy: 'date',
    sortDirection: 'desc',
    filters: []
  }
}

function cloneQuery (query) {
  var source = query || getDefaultQuery()
  return {
    page: source.page !== undefined ? source.page : 1,
    pageSize: source.pageSize !== undefined ? source.pageSize : 10,
    sortBy: source.sortBy || 'date',
    sortDirection: source.sortDirection || 'desc',
    filters: Array.isArray(source.filters)
      ? source.filters.map(function (f) {
        return {
          field: f.field,
          operator: f.operator,
          values: Array.isArray(f.values) ? f.values.slice() : []
        }
      })
      : []
  }
}

function normalizeCustomerReferenceKey (customerId) {
  if (customerId === null || customerId === undefined) return null
  var normalized = String(customerId).trim().toLowerCase()
  return normalized || null
}

function canUseLocalStorage () {
  return typeof window !== 'undefined' && !!window.localStorage
}

function readPersistedQueries () {
  if (!canUseLocalStorage()) return {}
  try {
    var raw = window.localStorage.getItem(MANUAL_ALLOCATIONS_QUERY_STORAGE_KEY)
    if (!raw) return {}
    var parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (error) {
    return {}
  }
}

function writePersistedQueries (map) {
  if (!canUseLocalStorage()) return
  try {
    window.localStorage.setItem(MANUAL_ALLOCATIONS_QUERY_STORAGE_KEY, JSON.stringify(map || {}))
  } catch (error) {
    // Ignore storage write failures (quota/private mode) and continue with in-memory state.
  }
}

function getPersistedQueryForCustomer (customerId) {
  var key = normalizeCustomerReferenceKey(customerId)
  if (!key) return null
  var map = readPersistedQueries()
  if (!map[key]) return null
  return cloneQuery(map[key])
}

function persistQueryForCustomer (customerId, query) {
  var key = normalizeCustomerReferenceKey(customerId)
  if (!key) return
  var map = readPersistedQueries()
  map[key] = cloneQuery(query)
  writePersistedQueries(map)
}

function ensureDefaultStatusFilter (state) {
  var hasStatus = state.query.filters.some(function (f) {
    return f.field === 'status'
  })
  if (!hasStatus) {
    state.query.filters = state.query.filters.concat([{
      field: 'status',
      operator: 'in',
      values: DEFAULT_STATUS_FILTER_VALUES.slice()
    }])
  }
}

export const state = {
  accountInfo: null,
  transactionSummary: null,
  allocations: [],

  // Mirrors the TransactionListPageModel POST body — passed directly to the API
  query: getDefaultQuery(),
  currentCustomerReferenceKey: null,

  // Populated from the response pagination object (PascalCase — matches QueenBee response shape, Amendment A001)
  pagination: {
    Page: 1,
    PageSize: 10,
    TotalItems: 0,
    TotalPages: 0
  },

  loading: false,
  error: null,      // inline errors: filter validation, fetchTransactions failures
  fatalError: null  // blocking errors: fetchDashboardViewModel failures
}

export const mutations = {
  setCustomerFilterContext (state, customerId) {
    state.currentCustomerReferenceKey = normalizeCustomerReferenceKey(customerId)
    var persisted = getPersistedQueryForCustomer(customerId)
    state.query = persisted || getDefaultQuery()
    ensureDefaultStatusFilter(state)
    if (state.currentCustomerReferenceKey) {
      persistQueryForCustomer(state.currentCustomerReferenceKey, state.query)
    }
  },

  setAccountInfo (state, accountInfo) {
    state.accountInfo = accountInfo
  },

  setTransactionSummary (state, transactionSummary) {
    state.transactionSummary = transactionSummary
  },

  setAllocations (state, allocations) {
    state.allocations = allocations
  },

  setPagination (state, pagination) {
    state.pagination = pagination
  },

  setLoading (state, loading) {
    state.loading = loading
  },

  setError (state, error) {
    state.error = error
  },

  setFatalError (state, fatalError) {
    state.fatalError = fatalError
  },

  setQuery (state, partial) {
    Object.assign(state.query, partial)
    if (state.currentCustomerReferenceKey) {
      persistQueryForCustomer(state.currentCustomerReferenceKey, state.query)
    }
  },

  resetQuery (state) {
    state.query = getDefaultQuery()
    if (state.currentCustomerReferenceKey) {
      persistQueryForCustomer(state.currentCustomerReferenceKey, state.query)
    }
  },

  // Single generic mutation — the ONLY place FilterItems are constructed (Constitution III)
  setAllocationsFilter (state, payload) {
    var field = payload.field
    var value = payload.value

    // Remove all existing FilterItems for the affected field
    var keep = state.query.filters.filter(function (f) {
      return f.field !== field
    })

    var newItems = []

    if (field === 'reference') {
      if (value) {
        newItems.push({ field: 'reference', operator: 'contains', values: [value] })
      }
    } else if (field === 'date') {
      // Date uses el-date-picker daterange — always provides both bounds or null
      if (value && Array.isArray(value) && value.length === 2) {
        newItems.push({ field: 'date', operator: 'gte', values: [value[0]] })
        newItems.push({ field: 'date', operator: 'lte', values: [value[1]] })
      }
      // null means cleared — both date FilterItems already removed via keep
    } else if (field === 'amount' || field === 'remainingBalance') {
      // Partial ranges are valid — only send populated side(s)
      if (value && value.from !== null && value.from !== undefined) {
        newItems.push({ field: field, operator: 'gte', values: [value.from] })
      }
      if (value && value.to !== null && value.to !== undefined) {
        newItems.push({ field: field, operator: 'lte', values: [value.to] })
      }
    } else if (field === 'status' || field === 'type') {
      if (value && Array.isArray(value) && value.length > 0) {
        newItems.push({ field: field, operator: 'in', values: value })
      }
    }

    state.query.filters = keep.concat(newItems)
    if (state.currentCustomerReferenceKey) {
      persistQueryForCustomer(state.currentCustomerReferenceKey, state.query)
    }
  }
}

export const actions = {
  initializeFiltersForCustomer ({ commit }, customerId) {
    commit('setCustomerFilterContext', customerId)
  },

  fetchDashboardViewModel ({ commit }, customerId) {
    var url = 'https://' + Vue.config.honeycomb.queenbee.domain +
      '/api/v1/viewmodels/manual-allocations-dashboard/' + customerId + '/summary'
    commit('setLoading', true)
    return Vue.prototype.$http.get(url, { withCredentials: true })
      .then(function (response) {
        // PascalCase response keys per Amendment A001
        commit('setAccountInfo', response.data.AccountInfo)
        commit('setTransactionSummary', response.data.TransactionSummary)
        commit('setLoading', false)
      })
      .catch(function () {
        commit('setFatalError', 'Unable to load dashboard data. Please try again later.')
        commit('setLoading', false)
      })
  },

  fetchTransactions ({ commit, state }, customerId) {
    var url = 'https://' + Vue.config.honeycomb.queenbee.domain +
      '/api/v1/viewmodels/manual-allocations-dashboard/' + customerId + '/transactions'
    commit('setLoading', true)
    return Vue.prototype.$http.post(url, state.query, { withCredentials: true })
      .then(function (response) {
        // PascalCase response keys per Amendment A001
        commit('setAllocations', response.data.Transactions)
        commit('setPagination', response.data.Pagination)
        commit('setLoading', false)
      })
      .catch(function () {
        commit('setError', 'Unable to load transactions. Please try again later.')
        commit('setLoading', false)
      })
  },

  applyFilters ({ commit, dispatch, state }, customerId) {
    // Validate date range (gte > lte is invalid)
    var dateGte = null
    var dateLte = null
    var amountGte = null
    var amountLte = null

    state.query.filters.forEach(function (f) {
      if (f.field === 'date' && f.operator === 'gte') { dateGte = f.values[0] }
      if (f.field === 'date' && f.operator === 'lte') { dateLte = f.values[0] }
      if (f.field === 'amount' && f.operator === 'gte') { amountGte = f.values[0] }
      if (f.field === 'amount' && f.operator === 'lte') { amountLte = f.values[0] }
    })

    if (dateGte !== null && dateLte !== null && dateGte > dateLte) {
      commit('setError', 'Invalid filter range \u2014 From date cannot exceed To date')
      return
    }

    if (amountGte !== null && amountLte !== null && amountGte > amountLte) {
      commit('setError', 'Invalid filter range \u2014 From amount cannot exceed To amount')
      return
    }

    commit('setQuery', { page: 1 })
    return dispatch('fetchTransactions', customerId)
  },

  resetFilters ({ commit, dispatch }, customerId) {
    commit('resetQuery')
    commit('setAllocationsFilter', { field: 'status', value: DEFAULT_STATUS_FILTER_VALUES.slice() })
    return dispatch('fetchTransactions', customerId)
  },

  fetchDashboard ({ dispatch }, customerId) {
    dispatch('fetchDashboardViewModel', customerId)
    dispatch('fetchTransactions', customerId)
  }
}

export default {
  state,
  mutations,
  actions
}
